import type { IntlShape } from 'react-intl'
import { parseApiErrorMessage } from '@/core/lib/utils'

type ActivityConsumer = { id: string; shortTitle: string }

/**
 * Error body returned by property-service for activity writes. ConflictError
 * merges its details at the body root (`consumers`, `inventorySourceId`), but
 * the spec documents them under `details`, so both placements are read.
 * ValidationError lists per-field causes under `fields` (non-prod only).
 */
type ActivityErrorBody = {
  message?: string
  code?: string
  consumers?: ActivityConsumer[]
  activityCode?: string
  field?: string
  details?: Record<string, unknown> & { consumers?: ActivityConsumer[] }
  data?: { activityCode?: string; activityId?: string }
  fields?: { field: string; message: string }[]
}

export const ACTIVITY_HAS_DEPENDENTS = 'activity_has_dependents'
export const ACTIVITY_INVENTORY_SHARED = 'activity_inventory_shared'
export const ACTIVITY_CODE_TAKEN = 'activity_code_taken'

// Form labels of the activity-fields keys, so a 400 `validation_error` names
// the input in the admin's language instead of the raw API path
// ("details.highlights.10").
const FIELD_LABEL_IDS: Record<string, string> = {
  description: 'activities.create.description',
  location: 'activities.fields.location',
  locationUrl: 'activities.fields.locationUrl',
  highlights: 'activities.fields.highlights',
  inclusions: 'activities.fields.inclusions',
  exclusions: 'activities.fields.exclusions',
  whatToBring: 'activities.fields.whatToBring',
  requirements: 'activities.fields.requirements',
  postBookingInfo: 'activities.fields.postBookingInfo',
  activityCode: 'activities.create.activityCode',
  durationMinutes: 'activities.fields.durationMinutes',
  languages: 'activities.fields.languages',
  minQuantity: 'activities.fields.minQuantity',
  minAge: 'activities.fields.minAge',
  maxAge: 'activities.fields.maxAge',
  priceDelta: 'activities.fields.priceDelta',
  blackouts: 'activities.blackouts.title',
  startDate: 'activities.fields.startDate',
  endDate: 'activities.fields.endDate',
}

const fieldLabel = (
  field: string | undefined,
  formatMessage: IntlShape['formatMessage']
) => {
  const key = field?.split('.').find((segment) => segment in FIELD_LABEL_IDS)
  return key ? formatMessage({ id: FIELD_LABEL_IDS[key] }) : undefined
}

const parseBody = (error: unknown): ActivityErrorBody | null => {
  if (!(error instanceof Error) || !error.message) return null
  try {
    const parsed: unknown = JSON.parse(error.message)
    return parsed && typeof parsed === 'object'
      ? (parsed as ActivityErrorBody)
      : null
  } catch {
    return null
  }
}

/**
 * Turns a failed activity / activity-inventory write into the toast text.
 * - 409 `activity_has_dependents` (archive/pause of an inventory source):
 *   names the consumers so the admin knows what to detach first.
 * - 409 `activity_inventory_shared` (monthly inventory write on a consumer):
 *   points to the source activity.
 * - 409 `activity_code_taken` (activity-fields): names the duplicated code.
 * - anything else: the API message plus the per-field causes, so a 400 like
 *   "slot 10:00-11:00 missing in source" reaches the user instead of the
 *   generic "Validation error"; activity-fields keys get their form label.
 */
export const describeActivityError = (
  error: unknown,
  formatMessage: IntlShape['formatMessage']
): string => {
  const body = parseBody(error)
  if (!body) return parseApiErrorMessage(error)

  if (body.code === ACTIVITY_HAS_DEPENDENTS) {
    const consumers = body.consumers ?? body.details?.consumers ?? []
    return formatMessage(
      { id: 'activities.errors.hasDependents' },
      { consumers: consumers.map((consumer) => consumer.shortTitle).join(', ') }
    )
  }
  if (body.code === ACTIVITY_INVENTORY_SHARED) {
    return formatMessage({ id: 'activities.errors.inventoryShared' })
  }
  if (body.code === ACTIVITY_CODE_TAKEN) {
    // ConflictError spreads `data` at the body root; the contract shows it
    // under `data`. Read every placement.
    const activityCode =
      body.activityCode ??
      body.data?.activityCode ??
      (typeof body.details?.activityCode === 'string'
        ? body.details.activityCode
        : '')
    return formatMessage(
      { id: 'activities.errors.activityCodeTaken' },
      { activityCode }
    )
  }

  const message = body.message || parseApiErrorMessage(error)
  const fieldCauses = (body.fields ?? []).map((field) => ({
    label: fieldLabel(field.field, formatMessage),
    message: field.message,
  }))
  if (fieldCauses.length === 0 && body.field) {
    fieldCauses.push({ label: fieldLabel(body.field, formatMessage), message })
  }
  // The validator usually repeats the field message as the top-level one;
  // then the label goes in front of it instead of repeating the text.
  const sameAsMessage = fieldCauses.find(
    (cause) => cause.label && cause.message === message
  )
  const head = sameAsMessage ? `${sameAsMessage.label}: ${message}` : message
  const causes = [
    ...fieldCauses
      .filter((cause) => cause !== sameAsMessage)
      .map((cause) =>
        cause.label ? `${cause.label}: ${cause.message}` : cause.message
      ),
    ...Object.values(body.details ?? {}).filter(
      (value): value is string => typeof value === 'string'
    ),
  ].filter((cause) => cause && cause !== message)
  const uniqueCauses = [...new Set(causes)]
  return uniqueCauses.length > 0 ? `${head}: ${uniqueCauses.join('; ')}` : head
}
